import React from "react";

import {
  Minus,
  Plus,
  Package2,
  Tag,
  DollarSign,
} from "lucide-react";

import "../css/productTable.css";

const ProductTable = ({
  cart,
  setCart,
  setSelectedProduct,
  selectedProduct,
  onIncQty,
  onDecQty,
  onSetQty,
}) => {

  /* ========================================================
      RESERVED QTY
  ======================================================== */
  const reservedQty = (
    productId,
    excludeIndex = -1
  ) =>
    cart.reduce(
      (sum, it, idx) =>
        it.id === productId &&
        idx !== excludeIndex
          ? sum +
            Number(
              it.quantity || 0
            )
          : sum,
      0
    );

  const fmt = (n) =>
    Number(n ?? 0).toFixed(2);

  /* ========================================================
      UI
  ======================================================== */
  return (
    <div className="modern-product-table-wrap">

      <table className="modern-product-table">

        {/* ====================================================
            HEAD
        ==================================================== */}
        <thead>

          <tr>

            <th>
              <div className="modern-th-content">
                <Package2 size={15} />
                <span>Product</span>
              </div>
            </th>

            <th
              style={{ width: 220 }}
            >
              Quantity
            </th>

            <th>
              <div className="modern-th-content">
                <DollarSign size={15} />
                <span>Price</span>
              </div>
            </th>

            <th>
              <div className="modern-th-content">
                <Tag size={15} />
                <span>Discount</span>
              </div>
            </th>

            <th>Amount</th>

          </tr>

        </thead>

        {/* ====================================================
            BODY
        ==================================================== */}
        <tbody>

          {cart.length === 0 ? (

            <tr>

              <td
                colSpan="5"
                className="modern-empty-cart"
              >

                <div className="modern-empty-state">

                  <div className="modern-empty-icon">
                    <Package2 size={24} />
                  </div>

                  <h4>
                    No Items Added
                  </h4>

                  <p>
                    Search or scan a
                    product to begin
                    transaction.
                  </p>

                </div>

              </td>

            </tr>

          ) : (

            cart.map(
              (
                product,
                index
              ) => {

                const quantity =
                  Number(
                    product.quantity ||
                      1
                  );

                const price =
                  Number(
                    product.price || 0
                  );

                const discount =
                  Number(
                    product.discount ||
                      0
                  );

                const discountType =
                  product.discountType ||
                  "";

                let discountDisplay =
                  "-";

                let discountAmount = 0;

                if (
                  discountType ===
                  "percent"
                ) {

                  discountDisplay =
                    `${discount}%`;

                  discountAmount =
                    price *
                    quantity *
                    (discount / 100);

                } else if (
                  discountType ===
                  "cash"
                ) {

                  discountDisplay =
                    `$${fmt(discount)}`;

                  discountAmount =
                    discount;
                }

                const netAmount =
                  price *
                    quantity -
                  discountAmount;

                /* =====================
                    STOCK
                ===================== */
                const usesStock =
                  !!product.uses_stock;

                const stock =
                  Number(
                    product.product_quantity ??
                      product.available_quantity ??
                      0
                  );

                const already =
                  reservedQty(
                    product.id,
                    index
                  );

                const left =
                  Math.max(
                    0,
                    stock - already
                  );

                const isSelected =
                  selectedProduct ===
                  product;

                return (

                  <tr
                    key={`${product.id}-${index}`}
                    onClick={() =>
                      setSelectedProduct(
                        product
                      )
                    }
                    className={
                      isSelected
                        ? "modern-selected-row"
                        : ""
                    }
                  >

                    {/* PRODUCT */}
                    <td>

                      <div className="modern-product-cell">

                        <div className="modern-product-avatar">
                          <Package2
                            size={16}
                          />
                        </div>

                        <div className="modern-product-info">

                          <span className="modern-product-name">
                            {
                              product.name
                            }
                          </span>

                          <span className="modern-product-meta">
                            ID:
                            {" "}
                            {
                              product.id
                            }
                          </span>

                        </div>

                      </div>

                    </td>

                    {/* QTY */}
                    <td>

                      {usesStock ? (

                        <div className="modern-qty-wrap">

                          <div className="modern-qty-box">

                            <button
                              className="modern-qty-btn"
                              onClick={(
                                e
                              ) => {
                                e.stopPropagation();

                                onDecQty?.(
                                  index
                                );
                              }}
                            >
                              <Minus
                                size={
                                  14
                                }
                              />
                            </button>

                            <input
                              className="modern-qty-input"
                              type="number"
                              min={1}
                              value={
                                quantity
                              }
                              onClick={(
                                e
                              ) =>
                                e.stopPropagation()
                              }
                              onChange={(
                                e
                              ) =>
                                onSetQty?.(
                                  index,
                                  Number(
                                    e
                                      .target
                                      .value ||
                                      1
                                  )
                                )
                              }
                            />

                            <button
                              className="modern-qty-btn"
                              disabled={
                                quantity >=
                                left
                              }
                              onClick={(
                                e
                              ) => {
                                e.stopPropagation();

                                onIncQty?.(
                                  index
                                );
                              }}
                            >
                              <Plus
                                size={
                                  14
                                }
                              />
                            </button>

                          </div>

                          <span className="modern-stock-left">
                            {left}
                            {" "}
                            left
                          </span>

                        </div>

                      ) : (

                        <span className="modern-no-stock">
                          {quantity}
                        </span>

                      )}

                    </td>

                    {/* PRICE */}
                    <td className="modern-price-cell">
                      ${fmt(price)}
                    </td>

                    {/* DISCOUNT */}
                    <td
                      className={`modern-discount-cell ${
                        discountAmount >
                        0
                          ? "active"
                          : ""
                      }`}
                    >
                      {
                        discountDisplay
                      }
                    </td>

                    {/* TOTAL */}
                    <td className="modern-total-cell">
                      $
                      {fmt(
                        netAmount
                      )}
                    </td>

                  </tr>
                );
              }
            )
          )}

        </tbody>

      </table>

    </div>
  );
};

export default ProductTable;